import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import RatingSelector from './RatingSelector';

export default function ManagerFeedbackModal({ isOpen, onClose, ikr, onSubmit, existingFeedback, managers, canEdit = true, evaluationMode, viewer }) {
  const [formData, setFormData] = useState({
    managerName: existingFeedback?.managerName || '',
    feedback: existingFeedback?.feedback || '',
    rating: existingFeedback?.rating || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (evaluationMode === 'manager' && viewer && !existingFeedback?.managerName) {
      setFormData(prev => ({ ...prev, managerName: viewer.name || '' }));
    } else if (existingFeedback?.managerName) {
      setFormData(prev => ({ ...prev, managerName: existingFeedback.managerName }));
    }
  }, [evaluationMode, viewer, existingFeedback?.managerName]);

  const handleSubmit = async () => {
    if (!formData.managerName.trim() || !formData.feedback.trim() || !formData.rating) {
      alert('Please fill all fields before submitting.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting manager feedback:', error);
      alert('Error submitting manager feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Manager Feedback</h2>
              <p className="text-sm text-gray-600 mt-1">{ikr?.text}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {evaluationMode === 'admin' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Who is providing this feedback?</label>
              <select value={formData.managerName} onChange={(e) => setFormData(prev => ({ ...prev, managerName: e.target.value }))} className={`w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={!canEdit}>
                <option value="">Select Manager/Admin</option>
                {managers.map(manager => <option key={manager.id} value={manager.name}>{manager.name} ({manager.role})</option>)}
              </select>
            </div>
          ) : evaluationMode === 'manager' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Manager Name</label>
              <input type="text" value={formData.managerName} className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-not-allowed" disabled readOnly />
            </div>
          ) : null}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Feedback</label>
            <textarea value={formData.feedback} onChange={(e) => setFormData(prev => ({ ...prev, feedback: e.target.value }))} placeholder="Provide detailed feedback..." className={`w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[150px] ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} rows={6} disabled={!canEdit} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
            <RatingSelector value={formData.rating} onChange={(value) => setFormData(prev => ({ ...prev, rating: value }))} disabled={!canEdit} />
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800" disabled={isSubmitting}>Cancel</button>
          {canEdit && (
            <button onClick={handleSubmit} disabled={isSubmitting || !formData.managerName.trim() || !formData.feedback.trim() || !formData.rating} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
